import React, { useContext, useEffect, useMemo } from 'react'
import { getLanguageCode } from 'src/business/services'
import {
  PaywallFeatureListContainer,
  PaywallGetTrialButtonPrimary,
  PaywallPlanCardContainer,
  PaywallPlanFeatureListItem,
  PaywallPlanName,
  PaywallPlanRecommendedText,
  PaywallPlanRecommendedTextPlaceholder,
  PaywallPlanSubtext,
  PlanMainDetailsContainer,
  PlanPriceText,
} from '../styledComponents'
import { getFormattedCount } from 'src/shared/utils/formatCount'
import Check from '@mui/icons-material/Check'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import { BusinessProfileContext } from 'src/business/context/BusinessProfileContext'
import { currency_list } from 'src/consumer/constants'
import {
  DialogContent,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material'
import { useState } from 'react'
import {
  DropDownPricePrimary,
  PaywallDropDownList,
  PaywallPlanCardInput,
  SessionNumberContent,
  SessionsButton,
} from './styledComponents'
import { useRef } from 'react'

export function BusinessSubscriptionPlanCard({
  type = 'secondary',
  themeType,
  planData,
  handlePayments,
  windowWidth,
  seats,
  setSeats,
}) {
  const { businessCustomerDetails } = useContext(BusinessProfileContext)
  const handlePlanClick = (planData) => {
    if (businessCustomerDetails?.billingDetails?.billingAddress?.countryCode === 'in') {
      handlePayments(
        'razorpay',
        planData?.id,
        planData?.translations?.[getLanguageCode()]?.subscriptionSuccessMsg,
      )
    } else {
      handlePayments('stripe', planData?.id, planData?.title)
    }
  }
  return (
    <PaywallPlanCardContainer recommended={planData?.popular} themeType={themeType}>
      <PlanMainDetailsContainer>
        {planData?.popular && <PaywallPlanRecommendedText>Recommended</PaywallPlanRecommendedText>}
        {!planData?.popular && <PaywallPlanRecommendedTextPlaceholder />}
        {planData?.translations?.[getLanguageCode()]?.title && (
          <PaywallPlanName>{planData?.translations?.[getLanguageCode()]?.title}</PaywallPlanName>
        )}
        {planData?.translations?.[getLanguageCode()]?.description && (
          <PaywallPlanSubtext>
            {planData?.translations?.[getLanguageCode()]?.description}
          </PaywallPlanSubtext>
        )}
        <PaywallPlanDropDownAndPricing planData={planData} seats={seats} setSeats={setSeats} />
        <PaywallGetTrialButtonPrimary onClick={() => handlePlanClick(planData)}>
          Get this Plan
        </PaywallGetTrialButtonPrimary>
      </PlanMainDetailsContainer>
      {planData?.benefits && (
        <PaywallFeatureListContainer>
          {planData?.benefits?.map((data, idx) => (
            <PaywallPlanFeatureListItem key={idx}>
              <Check />
              {planData?.translations?.[getLanguageCode()]?.benefits?.[idx]}
            </PaywallPlanFeatureListItem>
          ))}
        </PaywallFeatureListContainer>
      )}
    </PaywallPlanCardContainer>
  )
}

function PaywallPlanDropDownAndPricing({ planData, seats, setSeats }) {
  const currencyData = currency_list?.find(
    (currency) => currency?.code?.toUpperCase() === planData?.currency?.toUpperCase(),
  )
  const [optionButtonAnchor, setOptionButtonAnchor] = useState(null)
  const [groupSessionAnchor, setGroupSessionAnchor] = useState(null)
  const [dataToSubmit, setDataToSubmit] = useState({})
  const liveSessionsArray = useMemo(() => {
    return planData
      ? Array.from(
          {
            length: getNumberOfLiveSessionArrayLength(planData),
          },
          (v, i) => getNumberOfLiveSessionsPerItem(planData, i),
        )
      : []
  }, [planData])
  const groupSessionsArray = useMemo(() => {
    return planData
      ? Array.from(
          {
            length: getNumberOfGroupSessionsArrayLength(planData),
          },
          (v, i) => getNumberOfGroupSessionsPerItem(planData, i),
        )
      : []
  }, [planData])
  const priceToShow = useMemo(() => {
    return (
      ((planData?.price || 0) +
        (dataToSubmit?.groupSession?.groupSessionPrice || 0) +
        (dataToSubmit?.liveSession?.liveSessionPrice || 0)) /
      seats
    )
  }, [planData, dataToSubmit])
  function handleOpen(event) {
    setOptionButtonAnchor(event.currentTarget)
  }
  function handleOpenGroupSessionOptions(event) {
    setGroupSessionAnchor(event.currentTarget)
  }
  function handleClose() {
    setOptionButtonAnchor(null)
    setGroupSessionAnchor(null)
  }
  function handleSelectSession(id, dta) {
    setDataToSubmit((prev) => ({
      ...prev,
      [id]: dta,
    }))
    handleClose()
  }
  function handleSeatChange(event) {
    const value = event.target.value
    const toSet = value === '' ? '' : Number(value)
    setSeats(toSet)
  }

  useEffect(() => {
    if (!dataToSubmit?.liveSession) {
      setDataToSubmit((prev) => {
        return { ...prev, liveSession: liveSessionsArray?.[0] }
      })
    }
    if (!dataToSubmit?.groupSession) {
      setDataToSubmit((prev) => {
        return { ...prev, groupSession: groupSessionsArray?.[0] }
      })
    }
  }, [liveSessionsArray, groupSessionsArray])

  return (
    <>
      {planData?.price && (
        <PlanPriceText>
          {currencyData?.symbol} {getFormattedCount(priceToShow)}
          <span className="plan-price-per-person"> PSPM</span>
        </PlanPriceText>
      )}
      <BusinessPaywallInputComponent
        label="Number of employees"
        value={seats}
        type="number"
        onChange={handleSeatChange}
      />
      <SessionsButton onClick={handleOpen}>
        {!!dataToSubmit?.liveSession ? (
          <>
            <DropDownPricePrimary>
              <span>{dataToSubmit?.liveSession?.numberOfLiveSessions} Monthly Live Sessions</span>
              <span className="pricing">
                {currencyData?.symbol}
                {dataToSubmit?.liveSession?.liveSessionPrice} PSPM
              </span>
            </DropDownPricePrimary>
          </>
        ) : (
          <span>Select Number of Sessions</span>
        )}
        <KeyboardArrowDown />
      </SessionsButton>
      <SessionsButton onClick={handleOpenGroupSessionOptions}>
        {!!dataToSubmit?.groupSession ? (
          <>
            <DropDownPricePrimary>
              <span>
                {dataToSubmit?.groupSession?.numberOfGroupSessions} Monthly Group Sessions
              </span>
              <span className="pricing">
                {currencyData?.symbol}
                {dataToSubmit?.groupSession?.groupSessionPrice} per month
              </span>
            </DropDownPricePrimary>
          </>
        ) : (
          <span>Select Number of Group Sessions</span>
        )}
        <KeyboardArrowDown />
      </SessionsButton>
      <Popover
        anchorEl={optionButtonAnchor}
        open={Boolean(optionButtonAnchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <SessionNumberContent>
          <PaywallDropDownList dense={true}>
            {liveSessionsArray?.map((session, idx) => (
              <ListItemButton onClick={() => handleSelectSession('liveSession', session)} key={idx}>
                <ListItemText
                  sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                  disableTypography
                  primary={
                    <DropDownPricePrimary>
                      {session?.numberOfLiveSessions} Monthly Live Sessions
                    </DropDownPricePrimary>
                  }
                  secondary={`${currencyData?.symbol}${session?.liveSessionPrice} PSPM`}
                />
              </ListItemButton>
            ))}
          </PaywallDropDownList>
        </SessionNumberContent>
      </Popover>
      <Popover
        anchorEl={groupSessionAnchor}
        open={Boolean(groupSessionAnchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <SessionNumberContent>
          <PaywallDropDownList dense={true}>
            {groupSessionsArray?.map((session, idx) => (
              <ListItemButton
                onClick={() => handleSelectSession('groupSession', session)}
                key={idx}
              >
                <ListItemText
                  sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                  disableTypography
                  primary={
                    <DropDownPricePrimary>
                      {session?.numberOfGroupSessions} Monthly Group Sessions
                    </DropDownPricePrimary>
                  }
                  secondary={`${currencyData?.symbol}${session?.groupSessionPrice} per month`}
                />
              </ListItemButton>
            ))}
          </PaywallDropDownList>
        </SessionNumberContent>
      </Popover>
    </>
  )
}

function BusinessPaywallInputComponent(props) {
  const inputRef = useRef(null)
  function handleFocus() {
    inputRef.current.focus()
  }
  return (
    <PaywallPlanCardInput onClick={handleFocus}>
      {props.label && <label>{props?.label}</label>}
      <input ref={inputRef} {...props}></input>
    </PaywallPlanCardInput>
  )
}

function getNumberOfLiveSessionArrayLength(planData) {
  return (
    (planData?.liveSessionsPSPMMinimum || 0) +
    (maxLiveSessions - (planData?.liveSessionsPSPMMinimum || 0)) /
      (planData?.liveSessionsPSPMMultiple || 1)
  )
}

function getNumberOfLiveSessionsPerItem(planData, itemNumber) {
  const numberOfLiveSessions =
    planData?.liveSessionsPSPMMinimum + itemNumber * (planData?.liveSessionsPSPMMultiple || 1)
  const liveSessionPrice =
    planData?.liveSessionsPSPMPrice *
    numberOfLiveSessions *
    (planData?.liveSessionsPSPMMultipleDiscount / 100)
  return { numberOfLiveSessions, liveSessionPrice }
}

//group sessions functions

function getNumberOfGroupSessionsArrayLength(planData) {
  return (
    (planData?.groupSessionMinimum || 0) +
    (maxGroupSession - (planData?.groupSessionMinimum || 0)) / (planData?.groupSessionMultiple || 1)
  )
}
function getNumberOfGroupSessionsPerItem(planData, itemNumber) {
  const numberOfGroupSessions =
    planData?.groupSessionMinimum + itemNumber * (planData?.groupSessionMultiple || 1)
  const groupSessionPrice =
    planData?.groupSessionUnitPrice *
    numberOfGroupSessions *
    (planData?.groupSessionMultipleDiscount / 100)
  return { numberOfGroupSessions, groupSessionPrice }
}

const maxLiveSessions = 10
const maxGroupSession = 10
